import { useEffect, useRef } from 'react'

/** Re-run a fetch when the screen is likely to be stale.
 *
 *  Calendar data is the one thing in this app that changes without anyone
 *  touching the CRM: the sync poller pulls events in from Google every couple of
 *  minutes. A screen that loads once then never looks again shows yesterday's
 *  calendar until you reload it by hand, which is not what "two-way" should feel
 *  like.
 *
 *  Two triggers, because they catch different things:
 *   - **coming back to the tab** is the common one. You add something in Google
 *     Calendar, switch back, and it should be here.
 *   - **an interval** covers sitting on the page while a colleague or a phone
 *     books something.
 *
 *  Nothing polls while the tab is hidden — a background tab refetching every
 *  minute is wasted requests and a wasted database connection.
 */
export function useLiveRefresh(reload: () => void, everyMs = 60_000) {
  const fn = useRef(reload)
  fn.current = reload

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') fn.current()
    }
    const timer = window.setInterval(refreshIfVisible, everyMs)
    window.addEventListener('focus', refreshIfVisible)
    document.addEventListener('visibilitychange', refreshIfVisible)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', refreshIfVisible)
      document.removeEventListener('visibilitychange', refreshIfVisible)
    }
  }, [everyMs])
}
